// client/src/hooks/useOrders.js
// Custom React hooks for order data fetching

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchOrders, fetchOrderStats, fetchOrderById, fetchOrderHistory,
  triggerAudit, fetchAuditStatus, fetchAuditResults, fetchAuditMismatches,
  fetchWebhookEvents,
  triggerSync30d, fetchSync30dStatus,
  fetchImportStatus, fetchValidateStatus,
} from "../services/api";

// ─────────────────────────────────────────────
// useOrders - Fetch paginated orders list
// ─────────────────────────────────────────────
export const useOrders = (filters = {}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [lastFetch, setLastFetch] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(
    async (params = {}) => {
      // Cancel any pending request
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        const data = await fetchOrders({ ...filters, ...params });
        setOrders(data.orders || []);
        setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
        setLastFetch(new Date());
      } catch (err) {
        if (err.name !== "CanceledError") {
          setError(err.message || "Failed to fetch orders");
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)]
  );

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const refresh = () => load({ page: pagination.page });
  const nextPage = () => pagination.page < pagination.pages && load({ page: pagination.page + 1 });
  const prevPage = () => pagination.page > 1 && load({ page: pagination.page - 1 });

  return { orders, loading, error, pagination, lastFetch, refresh, nextPage, prevPage };
};

// ─────────────────────────────────────────────
// useOrderStats - Aggregated stats
// ─────────────────────────────────────────────
export const useOrderStats = (filters = {}) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.location) params.location = filters.location;
      if (filters.search) params.search = filters.search;
      const data = await fetchOrderStats(params);
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_from, filters.date_to, filters.location, filters.search]);

  useEffect(() => {
    load();
  }, [load]);

  return { stats, loading, error, refresh: load };
};

// ─────────────────────────────────────────────
// useOrderDetail - Single order detail
// ─────────────────────────────────────────────
export const useOrderDetail = (id) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) { setOrder(null); return; }
    setLoading(true);
    setError(null);
    fetchOrderById(id)
      .then((data) => setOrder(data.order))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  return { order, loading, error };
};

export const useOrderHistory = (id) => {
  const [history, setHistory] = useState([]);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setHistory([]);
      setLive(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchOrderHistory(id)
      .then((data) => {
        setHistory(data.history || []);
        setLive(data.live || null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  return { history, live, loading, error };
};

// ─────────────────────────────────────────────
// useAutoRefresh - Auto-refresh every N seconds
// ─────────────────────────────────────────────
export const useAutoRefresh = (callback, intervalSeconds = 60, enabled = false) => {
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(callback, intervalSeconds * 1000);
    return () => clearInterval(interval);
  }, [callback, intervalSeconds, enabled]);
};

// ─────────────────────────────────────────────
// useAudit - Layer 2 protection audit state
// ─────────────────────────────────────────────
export const useAudit = () => {
  const [summary, setSummary]   = useState(null);
  const [results, setResults]   = useState([]);
  const [mismatches, setMismatches] = useState([]);
  const [running, setRunning]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const [statusData, resultsData, mismatchData] = await Promise.all([
        fetchAuditStatus(),
        fetchAuditResults({ limit: 50 }),
        fetchAuditMismatches({ limit: 50 }),
      ]);
      setSummary(statusData.summary);
      setResults(resultsData.results || []);
      setMismatches(mismatchData.mismatches || []);
      setRunning(statusData.summary?.running || false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll while audit is running (every 3s), otherwise poll every 30s
  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, running ? 3000 : 30000);
    return () => clearInterval(interval);
  }, [loadStatus, running]);

  const runAudit = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setError(null);
    try {
      await triggerAudit();
      // Start fast polling once audit is triggered
      await loadStatus();
    } catch (err) {
      setError(err.message);
      setRunning(false);
    }
  }, [running, loadStatus]);

  return { summary, results, mismatches, running, loading, error, runAudit, refresh: loadStatus };
};

// ─────────────────────────────────────────────
// useWebhookEvents - Live webhook event feed
// ─────────────────────────────────────────────
export const useWebhookEvents = (pollInterval = 10) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchWebhookEvents({ limit: 50 });
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, pollInterval * 1000);
    return () => clearInterval(interval);
  }, [load, pollInterval]);

  return { events, loading, error, refresh: load };
};

// ─────────────────────────────────────────────
// useSync30d - Sync last 30 days orders + validate
// ─────────────────────────────────────────────
export const useSync30d = () => {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const data = await fetchSync30dStatus();
      if (!data || typeof data !== "object") {
        throw new Error("Invalid sync status response");
      }
      setStatus(data);
      if (!data.running && data.phase !== "idle") {
        stopPolling();
      }
      if (data.running && !intervalRef.current) {
        intervalRef.current = setInterval(poll, 2500);
      }
    } catch (err) {
      setError(err.message || "Failed to load sync status");
    }
  }, [stopPolling]);

  const startSync = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      await triggerSync30d();
      // Start polling
      stopPolling();
      poll(); // immediate first poll
      if (!intervalRef.current) {
        intervalRef.current = setInterval(poll, 2500);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setStarting(false);
    }
  }, [poll, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    poll();
    return stopPolling;
  }, [poll, stopPolling]);

  return { status, starting, error, startSync };
};

// ─────────────────────────────────────────────
// useImportStatus - Poll "Sync from ShipHero" job status
// ─────────────────────────────────────────────
export const useImportStatus = () => {
  const [status, setStatus] = useState(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  const poll = useCallback(async () => {
    try {
      const data = await fetchImportStatus();
      if (data && typeof data === "object") setStatus(data);
      if (!data?.running && intervalRef.current) stopPolling();
    } catch { /* ignore */ }
  }, [stopPolling]);

  // Start polling when running is detected
  const startPolling = useCallback(() => {
    stopPolling();
    poll();
    intervalRef.current = setInterval(poll, 2500);
  }, [poll, stopPolling]);

  useEffect(() => {
    poll(); // initial load to show last run
    return stopPolling;
  }, [poll, stopPolling]);

  return { status, startPolling };
};

// ─────────────────────────────────────────────
// useValidateStatus - Poll "Validate / Re-validate All" job status
// ─────────────────────────────────────────────
export const useValidateStatus = () => {
  const [status, setStatus] = useState(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  const poll = useCallback(async () => {
    try {
      const data = await fetchValidateStatus();
      if (data && typeof data === "object") setStatus(data);
      if (!data?.running && intervalRef.current) stopPolling();
    } catch { /* ignore */ }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    poll();
    intervalRef.current = setInterval(poll, 2500);
  }, [poll, stopPolling]);

  useEffect(() => {
    poll();
    return stopPolling;
  }, [poll, stopPolling]);

  return { status, startPolling };
};
